import { Container, Graphics, IDestroyOptions, Matrix, Renderer, Sprite, Texture, filters } from "pixi.js"
import { findRendererApp } from "./renderer_app"

/**
 * Graphics shown through a blur that is baked once, after drawing, instead of filtered every frame.
 *
 * A live BlurFilter costs several offscreen passes and render-target switches per frame, and every
 * visible storey carries its own cues. The artwork changes only on a full map render, so soften()
 * renders it through the blur into a texture at that point, outside the frame's filter stack.
 * Baking lazily inside the frame (cacheAsBitmap) is unsafe here: pixi 6 sends a nested filter's
 * output to the enclosing filter's target, and the world surface sits inside the sight mask.
 *
 * The bake itself waits for the next animation frame. The same filtered render-to-texture made from
 * inside the game's frame update (where soften() is called) comes out EMPTY in pixi 6.2 -- measured:
 * 0 opaque pixels there, 33155 for the identical bake between frames. Until it lands (and headless,
 * with no renderer), the drawing shows through a live blur, so no frame goes without it.
 */
export class SoftenedGraphics extends Graphics {
    readonly blur: InstanceType<typeof filters.BlurFilter>
    private baked?: Sprite
    private pendingBake?: number
    private stamps: { sprite: Sprite; matrix: Matrix }[] = []

    constructor(strength: number, quality: number) {
        super()
        this.blur = new filters.BlurFilter(strength, quality)
    }

    /**
     * Art drawn and baked along with the drawing, through `matrix` in local space: a shadow cast by a
     * picture rather than a shape. It shows live, under the same blur, until the bake lands. The
     * texture stays the caller's.
     */
    stamp(texture: Texture, matrix: Matrix): void {
        const sprite = new Sprite(texture)
        sprite.transform.setFromMatrix(matrix)
        this.addChild(sprite)
        this.stamps.push({ sprite, matrix })
    }

    /** Call after drawing. */
    soften(): void {
        this.discardBake()
        if (!this.geometry.graphicsData.length && !this.stamps.length) return
        this.filters = [this.blur]
        const renderer = findRendererApp()?.renderer as Renderer | undefined
        if (!renderer || typeof requestAnimationFrame === "undefined") return
        this.pendingBake = requestAnimationFrame(() => {
            this.pendingBake = undefined
            if (!this.destroyed) this.bake(renderer)
        })
    }

    clear(): this {
        this.discardBake()
        this.stamps.forEach(({ sprite }) => sprite.destroy())
        this.stamps = []
        return super.clear()
    }

    destroy(options?: IDestroyOptions | boolean): void {
        this.discardBake()
        this.blur.destroy()
        super.destroy(options)
    }

    protected _render(renderer: Renderer): void {
        // Once baked, only the softened child is drawn.
        if (!this.baked) super._render(renderer)
    }

    private bake(renderer: Renderer): void {
        // generateTexture keeps the world transform of a parented object, which would bake the camera in.
        let brush: Container = new Graphics(this.geometry)
        if (this.stamps.length) {
            const drawing = brush
            brush = new Container()
            brush.addChild(drawing)
            this.stamps.forEach(({ sprite, matrix }) => {
                const copy = new Sprite(sprite.texture)
                copy.transform.setFromMatrix(matrix)
                brush.addChild(copy)
            })
        }
        brush.filters = [this.blur]
        const region = brush.getLocalBounds().clone().pad(this.blur.padding)
        region.ceil()
        const texture = renderer.generateTexture(brush, { region, resolution: this.blur.resolution })
        brush.filters = null
        brush.destroy({ children: true })
        this.filters = null
        this.stamps.forEach(({ sprite }) => { sprite.visible = false })
        this.baked = new Sprite(texture)
        this.baked.position.set(region.x, region.y)
        this.addChild(this.baked)
    }

    private discardBake(): void {
        if (this.pendingBake !== undefined) {
            cancelAnimationFrame(this.pendingBake)
            this.pendingBake = undefined
        }
        this.filters = null
        this.stamps.forEach(({ sprite }) => { sprite.visible = true })
        if (!this.baked) return
        this.baked.destroy({ texture: true, baseTexture: true })
        this.baked = undefined
    }
}
